import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Typography } from "@material-tailwind/react";
import CustomAccordion from "components/Accordion/CustomAccordion";
import { motion } from "framer-motion";
import React, { FC, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { menuConfig } from "./menuConfig";
import { LayoutDashboard, Sparkles } from "lucide-react";

export interface DashboardMenuProps {
  children?: React.ReactNode;
}

const DashboardMenu: FC<DashboardMenuProps> = ({ children }) => {
  const [open, setOpen] = useState<number>(0);
  const location = useLocation();
  const [activeSubItem, setActiveSubItem] = useState<string | null>(location.pathname);

  const handleSubItemClick = (path: string) => {
    setActiveSubItem(path);
  };

  const handleOpen = (value: number) => {
    setOpen(open === value ? 0 : value);
  };

  useEffect(() => {
    setActiveSubItem(location.pathname);
    const activeMenu = menuConfig.find((menu: any) =>
      menu.subItems?.some((item: any) => item.to === location.pathname)
    );
    setOpen(activeMenu ? activeMenu.id : 0);
  }, [location]);

  return (
    <div className="flex flex-col min-h-screen lg:flex-row bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <motion.div
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="lg:w-80 bg-white dark:bg-gray-900 shadow-2xl border-r border-gray-200 dark:border-gray-700"
      >
        <div className="h-full overflow-auto">
          {/* Header Section */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="flex items-center space-x-3"
            >
              <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <Typography variant="h5" className="text-gray-900 dark:text-white font-bold">
                  Admin Panel
                </Typography>
                <p className="text-sm text-gray-500 dark:text-gray-400">EventToBee Dashboard</p>
              </div>
            </motion.div>
          </div>

          {/* Navigation */}
          <div className="p-6">
            <nav className="space-y-2">
              <motion.div whileHover={{ x: 4 }} whileTap={{ scale: 0.98 }} transition={{ duration: 0.2 }}>
                <Link
                  to="/dashboard"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-2xl transition-all duration-200 group ${
                    location.pathname === "/dashboard"
                      ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 dark:hover:from-purple-900/20 dark:hover:to-pink-900/20"
                  }`}
                >
                  <div
                    className={`p-2 rounded-xl ${
                      location.pathname === "/dashboard"
                        ? "bg-white/20"
                        : "bg-gray-100 dark:bg-gray-800 group-hover:bg-gradient-to-r group-hover:from-purple-100 group-hover:to-pink-100 dark:group-hover:from-purple-800 dark:group-hover:to-pink-800"
                    }`}
                  >
                    <LayoutDashboard className="w-5 h-5" />
                  </div>
                  <span className="font-medium">Tableau de bord</span>
                  {location.pathname === "/dashboard" && (
                    <motion.div layoutId="activeIndicator" className="ml-auto w-2 h-2 bg-white rounded-full" />
                  )}
                </Link>
              </motion.div>
              {menuConfig.map((menu: any) =>
                menu.subItems ? (
                  <CustomAccordion
                    key={menu.id}
                    open={open === menu.id}
                    handleOpen={() => handleOpen(menu.id)}
                    title={menu.title}
                    icon={menu.icon}
                    subItems={menu.subItems}
                    customIcon={
                      <ChevronDownIcon
                        className={`h-4 w-4 transition-transform dark:text-white ${open === menu.id ? "rotate-180" : ""}`}
                      />
                    }
                    activeSubItem={activeSubItem}
                    handleSubItemClick={handleSubItemClick}
                  />
                ) : (
                  <motion.div
                    key={menu.id}
                    whileHover={{ x: 4 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Link
                      to={menu.to!}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-2xl transition-all duration-200 group ${
                        location.pathname === menu.to
                          ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg"
                          : "text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 dark:hover:from-purple-900/20 dark:hover:to-pink-900/20"
                      }`}
                    >
                      <div
                        className={`p-2 rounded-xl ${
                          location.pathname === menu.to
                            ? "bg-white/20"
                            : "bg-gray-100 dark:bg-gray-800 group-hover:bg-gradient-to-r group-hover:from-purple-100 group-hover:to-pink-100 dark:group-hover:from-purple-800 dark:group-hover:to-pink-800"
                        }`}
                      >
                        {menu.icon}
                      </div>
                      <span className="font-medium">{menu.title}</span>
                      {location.pathname === menu.to && (
                        <motion.div layoutId="activeIndicator" className="ml-auto w-2 h-2 bg-white rounded-full" />
                      )}
                    </Link>
                  </motion.div>
                )
              )}
            </nav>
          </div>
        </div>
      </motion.div>

      {/* Main Content Area */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="flex-1 min-h-screen bg-transparent"
      >
        <div className="p-6 lg:p-8">
          <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg rounded-3xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 min-h-[calc(100vh-6rem)]">
            <div className="p-8">{children}</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default DashboardMenu;
